export default class CanvasFrameRecorder {
  /**
   * Creates a recorder for canvas content without audio
   * @param {HTMLCanvasElement} canvasElement - The canvas element to record
   * @param {number} frameRate - Frame rate for canvas capture (default: 30)
   */
  constructor(canvasElement, frameRate = 30) {
    this.canvasElement = canvasElement
    this.frameRate = frameRate
    this.mediaRecorder = null
    this.recordedChunks = []
    this.isRecording = false
    this.recordingTimeout = null
  }

  /**
   * Start recording the canvas content
   * @returns {Promise<boolean>} - Promise resolving to true if recording started successfully
   */
  async startRecording(audioStream = null) {
    try {
      // Reset chunks
      this.recordedChunks = []

      // Get the canvas stream - this only captures what's drawn on the canvas
      const canvasStream = this.canvasElement.captureStream(this.frameRate)

      if (audioStream) {
        audioStream.getAudioTracks().forEach((track) => {
          canvasStream.addTrack(track)
        })
      }

      // Find a supported MIME type
      const mimeTypes = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4',
      ]

      let selectedMimeType
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType
          break
        }
      }

      if (!selectedMimeType) {
        throw new Error('No supported MIME type found for recording')
      }

      // Create MediaRecorder with appropriate options
      this.mediaRecorder = new MediaRecorder(canvasStream, {
        mimeType: selectedMimeType,
        videoBitsPerSecond: 5000000,
      })

      // Set up data handling
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data)
          console.log('Chunk received:', event.data.size, 'bytes')
        }
      }

      // Start the recording
      this.mediaRecorder.start(100) // Collect data in 100ms chunks
      this.isRecording = true
      console.log('Canvas recording started with MIME type:', selectedMimeType)

      // Safety timeout to ensure we get data
      this.recordingTimeout = setTimeout(() => {
        if (this.recordedChunks.length === 0) {
          this.mediaRecorder.requestData()
        }
      }, 1000)

      return true
    } catch (error) {
      console.error('Error starting canvas recording:', error)
      throw error
    }
  }

  /**
   * Stop recording and get the recorded blob
   * @returns {Promise<Blob>} A promise that resolves with the recorded video blob
   */
  stopRecording() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('No active recording'))
        return
      }

      // Clear the safety timeout
      if (this.recordingTimeout) {
        clearTimeout(this.recordingTimeout)
        this.recordingTimeout = null
      }

      this.mediaRecorder.onstop = () => {
        if (this.recordedChunks.length === 0) {
          // Request data one last time before declaring no data
          this.mediaRecorder.requestData()
          setTimeout(() => {
            if (this.recordedChunks.length === 0) {
              reject(new Error('No data was recorded'))
              return
            }
            this.finalizeRecording(resolve)
          }, 200)
        } else {
          this.finalizeRecording(resolve)
        }
      }

      this.mediaRecorder.stop()
    })
  }

  /**
   * Create the final video blob from recorded chunks
   * @param {Function} resolve - Promise resolve function to call with the blob
   */
  finalizeRecording(resolve) {
    const blob = new Blob(this.recordedChunks, {
      type: this.mediaRecorder.mimeType,
    })

    console.log('Created canvas recording blob of size:', blob.size)
    this.isRecording = false
    resolve(blob)
  }

  /**
   * Download the recorded video
   * @param {string} filename - Name for the downloaded file
   * @throws {Error} If no recording is available
   */
  download(filename = 'canvas-recording.webm') {
    if (this.recordedChunks.length === 0) {
      throw new Error('No recorded data available')
    }

    const blob = new Blob(this.recordedChunks, {
      type: this.mediaRecorder.mimeType,
    })

    if (blob.size === 0) {
      throw new Error('Generated blob has zero size')
    }

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.style.display = 'none'
    a.href = url
    a.download = filename
    a.click()

    setTimeout(() => {
      URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }, 100)
  }
}
